"use client";
import Icon from "@/components/icons/Icons";
import CustomPageLoader from "@/components/others/CustomPageLoader";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { SearchedFunds } from "@/type/schemeType";
import { apiGet } from "@/utils/apiClient";
import { ROOT_URL } from "@/utils/gloableVariables";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";
import { motion } from "framer-motion";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useRef, useState, useTransition } from "react";

type FundSearchBarCompareProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  slug: string;
  isPendingParent: boolean;
};

export default function FundSearchBarCompare({ isOpen, onOpen, onClose, slug, isPendingParent }: FundSearchBarCompareProps) { 
  const PAGE_SIZE = 100;
  const separator = "-vs-";

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [funds, setFunds] = useState<SearchedFunds[]>([]);
  const [selectedFund, setSelectedFund] = useState<SearchedFunds | null>(null);
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const [isPendingChild, startTransition] = useTransition();

  const [isLoading, setisLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLLabelElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const router = useRouter();

  /* --------------- Compared URLs from route -----------------*/
  const comparedUrls = useMemo(() => {
    return new Set(slug.split(separator));
  }, [slug]);

  /* --------------- Fetch all funds once ------------------*/
  useEffect(() => {
    const fetchFunds = async () => {
      setisLoading(true);
      try {
        const res = await apiGet("/api/getGrowthFunds", { base: "none" });

        if (!res?.data?.length) throw new Error("Failed to fetch funds");

        const uniqueFundsMap = new Map<string, SearchedFunds>();

        (res.data as SearchedFunds[]).forEach((fund) => {
          if (!uniqueFundsMap.has(fund.url)) {
            uniqueFundsMap.set(fund.url, fund);
          }
        });

        setFunds(Array.from(uniqueFundsMap.values()));
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setisLoading(false);
      }
    };

    fetchFunds();
  }, []);

  /* -------------- Debounce search ----------------*/
  useEffect(() => {
    if (!search) {
      setDebouncedSearch("");
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  /* ------------- Base funds (remove compared) ----------------*/
  const baseFunds = useMemo(() => {
    return funds.filter((fund) => !comparedUrls.has(fund.url));
  }, [funds, comparedUrls]);

  /* ----------- Final displayed list ------------------*/
  const displayedFunds = useMemo(() => {
    if (!debouncedSearch) return baseFunds;

    const tokens = debouncedSearch.split(" ").filter(Boolean);

    const ranked = baseFunds
      .map((fund) => {
        const name = fund.scheme_name.toLowerCase();

        let score = 0;

        tokens.forEach((token) => {
          if (name.startsWith(token)) score += 20;
          else if (name.includes(token)) score += 10;
        });

        return { fund, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((item) => item.fund);

    return ranked;
  }, [baseFunds, debouncedSearch]);

  const paginatedFunds = useMemo(() => {
    return displayedFunds.slice(0, visibleCount);
  }, [displayedFunds, visibleCount]);

  /* ------------ Handle select fund -------------*/
  useEffect(() => {
    if (!selectedFund?.url) return;
    let nextSlug: string;

    if (!slug) {
      nextSlug = selectedFund.url;
    } else {
      const parts = slug.split(separator).filter(Boolean);

      if (parts.length < 4) {
        nextSlug = [...parts, selectedFund.url].join(separator);
      } else {
        const updated = [...parts.slice(0, 3), selectedFund.url];
        nextSlug = updated.join(separator);
      }
    }

    const currentPath = `/mutual-fund/compare/${slug}`;

    const targetPath = `/mutual-fund/compare/${nextSlug}`;

    if (currentPath !== targetPath) {
      startTransition(() => {
        router.push(targetPath);
      });
    }
  }, [selectedFund, router, slug]);

  /* -------------- Helpers ---------------*/
  const clearSearch = useCallback(() => {
    setSearch("");
  }, []);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  /* -------- UI ----------------*/
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [debouncedSearch]);

  /* -------- Page Loading Stop Scroll ----------------*/
  useEffect(() => {
    if (isPendingChild || isPendingParent) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
  }, [isPendingChild, isPendingParent]);

  const hasMoreFunds = displayedFunds.length > visibleCount;

  return (
    <>
      <div className="relative z-5 w-full max-w-lg mx-auto">
        <label ref={dropdownRef} onClick={onOpen} className="w-full flex items-center gap-2 py-3 px-5 rounded-full bg-white border border-[#8AA6D5] test-sm sm:text-xl text-black/50 transition duration-300 hover:border-[#4170bf] focus-within:border-[#4170bf]">
          <Icon.searchIcon2 className="shrink-0 text-primaryBlue/80" />

          <input ref={inputRef} type="text" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-transparent placeholder:text-primaryBlue/60" placeholder="Search & Add Mutual Funds" />

          {search && (
            <button onClick={clearSearch} className="ml-auto shrink-0">
              <Icon.crossIcon className="w-4 h-4 hover:text-primaryBlue" />
            </button>
          )}
        </label>

        <CustomDropdown isOpen={isOpen} onClose={onClose} excludeRefs={[dropdownRef]} className="bg-white rounded-2xl overflow-hidden w-full mt-1">
          <div className="max-h-60 overflow-y-auto space-y-1 px-1.5 py-3 sm:py-4 scrollbar-sm">
            {isLoading ? (
              <p className="px-2 text-sm text-primaryBlack/70 text-center">Loading funds... Please wait!</p>
            ) : error ? (
              <p className="px-2 text-sm text-red-500 text-center">Error: {error}</p>
            ) : paginatedFunds.length === 0 ? (
              <p className="px-2 text-sm text-primaryBlack/70 text-center">No matching funds found</p>
            ) : (
              paginatedFunds.map((fund) => (
                <button
                  key={fund.url}
                  onClick={() => {
                    setSelectedFund(fund);
                    onClose();
                  }}
                  className="w-full text-left p-2 transition duration-300 bg-white hover:bg-[#e6ebfd] rounded-xl flex gap-3 items-center"
                >
                  <Image src={`${ROOT_URL}/img/AMCsLogo/${fund.amc_code}.png`} alt={fund.scheme_name} width={30} height={30} className="h-7 w-7 sm:h-8 sm:w-8 border border-black/15 rounded-full object-contain shrink-0" onError={(e) => ((e.target as HTMLImageElement).src = `${ROOT_URL}/img/AMCsLogo/default.png`)} />

                  <p className="text-sm font-medium line-clamp-1">{fund.scheme_name}</p>
                </button>
              ))
            )}
            {hasMoreFunds && (
              <button onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)} className="w-full mt-3 py-2 rounded-xl border border-primaryBlue text-primaryBlue hover:bg-primaryBlue hover:text-white transition duration-300 text-sm font-medium">
                Load more funds
              </button>
            )}
          </div>
        </CustomDropdown>
      </div>

      {isOpen && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35, ease: "easeInOut" }} className="fixed inset-0 z-4 bg-black/60 backdrop-blur-md">
      
      </motion.div>}

      {(isPendingChild || isPendingParent) && <CustomPageLoader />}
    </>
  );
}
